import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import extractText from 'flarum/common/utils/extractText';

/**
 * Decorates each discussion row to match the Bespoke design, depending on the
 * active discussion-list mode (read off the <html> data-attribute):
 *
 *  - grid:     a cover image pulled from the first post's first <img>, plus a
 *              stack of participant ("viewed by") avatars in the footer.
 *  - category: a coloured left edge per primary tag, for the classic forum feel.
 *
 * Everything is guarded and idempotent — it must never break the list, and it
 * re-runs safely on Mithril redraws (the querySelector guards prevent dupes).
 */

function discMode() {
  return document.documentElement.getAttribute('data-bespoke-discussions') || 'feed';
}
function firstImage(html) {
  if (!html) return null;
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m && m[1] ? m[1] : null;
}

/** Plain-text excerpt from post HTML, trimmed to ~length. */
function excerptOf(html, len = 150) {
  if (!html) return '';
  // DOMParser builds an inert document: unlike innerHTML on a live element,
  // an <img onerror> in the source never loads or runs.
  const text = (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
  return text.length > len ? text.slice(0, len).trim() + '…' : text;
}

/** Compact relative time ("2h ago", "3d ago", "5mo ago") — no dependency. */
function humanTimeShort(d) {
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return s + 's ago';
  const m = Math.round(s / 60);
  if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60);
  if (h < 24) return h + 'h ago';
  const days = Math.round(h / 24);
  if (days < 7) return days + 'd ago';
  const w = Math.round(days / 7);
  if (w < 5) return w + 'w ago';
  const mo = Math.round(days / 30);
  if (mo < 12) return mo + 'mo ago';
  return Math.round(days / 365) + 'y ago';
}

// ---- individual decorations — each guarded + idempotent, so they can be read,
//      tested and changed in isolation. Called in sequence by decorate(). ----

/** The theme's default card cover (Discussions ▸ Default cover image), if set. */
function defaultCover() {
  try {
    const doc = app.bespoke?.document || app.forum.attribute('bespoke.published');
    const url = String(doc?.tokens?.layout?.cardCover || '').trim();
    return /^(https?:)?\//i.test(url) ? url : null;
  } catch (e) {
    return null;
  }
}

/** Grid: cover image pulled from the first post's first <img>, falling back
 *  to the theme's default cover so imageless cards keep the same silhouette.
 *
 *  Post-image covers are permanent; default-sourced covers re-evaluate on
 *  every decoration pass, so a real image arriving late replaces the
 *  fallback, an edited default URL swaps in, and clearing the setting
 *  removes them (the deco key includes the default URL — see decorate()). */
function decorateCover(el, content, d) {
  const fp = d.firstPost && d.firstPost();
  const postSrc = fp ? firstImage(fp.contentHtml && fp.contentHtml()) : null;
  const src = postSrc || defaultCover();
  const existing = content.querySelector('.DiscussionListItem-bespokeCover');
  if (existing) {
    if (existing.dataset.coverOrigin !== 'default') return;
    if (!src) {
      existing.remove();
      el.classList.remove('has-bespoke-cover');
      return;
    }
    if (existing.dataset.coverSrc !== src) {
      existing.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
      existing.dataset.coverSrc = src;
      existing.dataset.coverOrigin = postSrc ? 'post' : 'default';
    }
    return;
  }
  if (!src) return;
  const cover = document.createElement('div');
  cover.className = 'DiscussionListItem-bespokeCover';
  cover.dataset.coverOrigin = postSrc ? 'post' : 'default';
  cover.dataset.coverSrc = src;
  cover.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
  content.insertBefore(cover, content.firstChild);
  el.classList.add('has-bespoke-cover');
}

/** Grid: plain-text excerpt from the first post, under the title. */
function decorateExcerpt(content, d) {
  if (content.querySelector('.DiscussionListItem-bespokeExcerpt')) return;
  const fp = d.firstPost && d.firstPost();
  const ex = fp ? excerptOf(fp.contentHtml && fp.contentHtml()) : '';
  if (!ex) return;
  const exEl = document.createElement('p');
  exEl.className = 'DiscussionListItem-bespokeExcerpt';
  exEl.textContent = ex;
  content.appendChild(exEl);
}

/** Grid: nested "last reply" preview — the last poster's avatar + name and a
 *  short excerpt of their message. Only shown when there's an actual reply (the
 *  last post isn't the opening post), so single-post threads don't echo the
 *  first-post excerpt above. Needs lastPost + lastPost.user in the payload
 *  (DiscussionListMutator eager-loads them in grid mode). */
function decorateLastReply(content, d) {
  if (content.querySelector('.DiscussionListItem-bespokeLastReply')) return;
  const lp = d.lastPost && d.lastPost();
  if (!lp) return;
  const fp = d.firstPost && d.firstPost();
  if (fp && lp.id && fp.id && lp.id() === fp.id()) return; // no reply yet
  const author = lp.user && lp.user() || d.lastPostedUser && d.lastPostedUser();
  const ex = excerptOf(lp.contentHtml && lp.contentHtml(), 100);
  if (!ex && !author) return;
  const wrap = document.createElement('div');
  wrap.className = 'DiscussionListItem-bespokeLastReply';
  if (author) wrap.appendChild(avatar(author));
  const body = document.createElement('div');
  body.className = 'DiscussionListItem-bespokeLastReplyBody';
  const head = document.createElement('div');
  head.className = 'DiscussionListItem-bespokeLastReplyHead';
  if (author) {
    const nm = document.createElement('span');
    nm.className = 'DiscussionListItem-bespokeLastReplyName';
    nm.textContent = author.displayName && author.displayName() || '';
    head.appendChild(nm);
  }
  const when = lp.createdAt && lp.createdAt();
  if (when) {
    const dt = when instanceof Date ? when : new Date(when);
    if (!isNaN(dt.getTime())) {
      const tm = document.createElement('span');
      tm.className = 'DiscussionListItem-bespokeLastReplyTime';
      tm.textContent = humanTimeShort(dt);
      try {
        tm.title = dt.toLocaleString();
      } catch (e) {
        /* noop */
      }
      head.appendChild(tm);
    }
  }
  if (head.childNodes.length) body.appendChild(head);
  const msg = document.createElement('span');
  msg.className = 'DiscussionListItem-bespokeLastReplyText';
  msg.textContent = ex;
  body.appendChild(msg);
  wrap.appendChild(body);
  content.appendChild(wrap);
}

/** Grid: tag pills above the footer (Flarum's own tags hidden via CSS). */
function decorateTags(content, d) {
  if (content.querySelector('.DiscussionListItem-bespokeTags') || !d.tags) return;
  const tags = (d.tags() || []).filter(Boolean);
  if (!tags.length) return;
  const tagsEl = document.createElement('div');
  tagsEl.className = 'DiscussionListItem-bespokeTags';
  tags.slice(0, 4).forEach(tag => {
    const pill = document.createElement('a');
    pill.className = 'Bespoke-tagPill';
    pill.textContent = tag.name && tag.name() || '';
    const c = tag.color && tag.color();
    if (c) {
      pill.style.background = c;
      pill.style.color = '#fff';
      pill.style.borderColor = 'transparent';
    }
    tagsEl.appendChild(pill);
  });
  content.appendChild(tagsEl);
}

/** Grid: dedicated card footer — replier avatars (left) + reply count (right). */
function decorateCardFooter(el, content, d) {
  if (content.querySelector('.DiscussionListItem-bespokeFoot')) return;
  const seen = new Set();
  const users = [];
  const add = u => {
    if (u && u.id && !seen.has(u.id())) {
      seen.add(u.id());
      users.push(u);
    }
  };
  add(d.user && d.user()); // author
  const mrp = d.mostRelevantPost && d.mostRelevantPost();
  if (mrp && mrp.user) add(mrp.user()); // most relevant poster
  add(d.lastPostedUser && d.lastPostedUser()); // last poster

  const foot = document.createElement('div');
  foot.className = 'DiscussionListItem-bespokeFoot';
  const viewers = document.createElement('div');
  viewers.className = 'DiscussionListItem-bespokeViewers';
  users.slice(0, 4).forEach(u => viewers.appendChild(avatar(u)));
  const total = d.participantCount && d.participantCount() || users.length;
  const extra = total - Math.min(users.length, 4);
  if (extra > 0) {
    const more = document.createElement('span');
    more.className = 'more';
    more.textContent = '+' + extra;
    viewers.appendChild(more);
  }
  const replies = document.createElement('span');
  replies.className = 'DiscussionListItem-bespokeReplies';
  const n = (d.replyCount && d.replyCount()) ?? 0;
  replies.textContent = extractText(app.translator.trans('ernestdefoe-bespoke.forum.replies_count', {
    count: n
  }));
  const stats = document.createElement('div');
  stats.className = 'DiscussionListItem-bespokeStats';
  const views = document.createElement('span');
  views.className = 'DiscussionListItem-bespokeViews';
  const vc = Number(d.attribute && d.attribute('bespokeViews')) || 0;
  views.title = extractText(app.translator.trans('ernestdefoe-bespoke.forum.views_count', {
    count: vc
  }));
  views.innerHTML = '<i class="icon fa-solid fa-eye"></i> ';
  views.appendChild(document.createTextNode(fmtCount(vc)));
  stats.appendChild(views);
  stats.appendChild(replies);
  foot.appendChild(viewers);
  foot.appendChild(stats);
  content.appendChild(foot);
  el.classList.add('has-bespoke-foot'); // only then do we hide the default stats
}

/** Category mode: a coloured left edge from the primary tag (cleared otherwise). */
function decorateCategoryBorder(el, d, mode) {
  if (mode === 'category' && d.tags) {
    const pt = primaryTag(d);
    const color = pt && pt.color && pt.color();
    el.style.borderLeft = color ? '4px solid ' + color : '';
  } else {
    el.style.borderLeft = '';
  }
}

/**
 * Per-card tag accent (Effects ▸ Card tag accent). Colour each card by ITS OWN
 * primary tag so a mixed list shows a rainbow of accents (not one global brand
 * colour); the CSS consumes --bespoke-card-accent with a brand fallback.
 */
function decorateCardAccent(el, d, borders) {
  if (borders !== 'none') {
    const pt = primaryTag(d);
    const c = pt && pt.color && pt.color();
    if (c) el.style.setProperty('--bespoke-card-accent', c);else el.style.removeProperty('--bespoke-card-accent');
  } else {
    el.style.removeProperty('--bespoke-card-accent');
  }
}

/**
 * New-post indicator. Cheap (one attr read + class toggle), so it runs on EVERY
 * redraw — read state changes between redraws (e.g. right after visiting a
 * thread). Shows a "has unread" dot in every layout, notably grid where the
 * default reply-count badge is hidden.
 */
function markUnread(el, d) {
  try {
    el.classList.toggle('has-bespoke-unread', !!(d.isUnread && d.isUnread()));
  } catch (e) {
    console.warn('[bespoke]', e);
  }
}

/**
 * Slim dispatcher. Always refreshes the (cheap) unread indicator, then runs the
 * heavier decorations only when the layout mode / card-accent setting changes —
 * gated by a per-row dirty key so onupdate (fired on every m.redraw) doesn't
 * thrash layout across 20+ rows.
 */
function decorate() {
  const el = this.element;
  const d = this.attrs && this.attrs.discussion;
  if (!el || !d) return;
  // The discussion-view fly-out pane reuses DiscussionListItem; keep it plain.
  if (el.closest('.DiscussionListPane')) return;
  markUnread(el, d);
  const mode = discMode();
  const borders = document.documentElement.getAttribute('data-bespoke-borders') || 'none';
  // The default-cover URL is part of the dirty key so editing/clearing it in
  // the theme editor re-runs decoration (decorateCover swaps or removes
  // default-sourced covers; post-image covers are left alone).
  const decoKey = mode + '|' + borders + '|' + (defaultCover() || '');
  if (el.getAttribute('data-bespoke-deco') === decoKey) return;
  try {
    if (mode === 'grid') {
      const content = el.querySelector('.DiscussionListItem-content');
      if (content) {
        decorateCover(el, content, d);
        decorateExcerpt(content, d);
        decorateTags(content, d);
        decorateLastReply(content, d);
        decorateCardFooter(el, content, d);
      }
    }
    decorateCategoryBorder(el, d, mode);
    decorateCardAccent(el, d, borders);
    // Mark decorated for this mode/border combo so the next redraw skips the work.
    el.setAttribute('data-bespoke-deco', decoKey);
  } catch (e) {
    console.warn('[bespoke]', e); // never break the list, but make it discoverable
  }
}

/** 1.2K-style compact count for the card stats. */
function fmtCount(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}
function avatar(u) {
  const av = document.createElement('span');
  av.className = 'Avatar';
  const url = u.avatarUrl && u.avatarUrl();
  if (url) {
    av.style.backgroundImage = 'url("' + url + '")';
    av.style.backgroundSize = 'cover';
    av.style.backgroundPosition = 'center';
  } else {
    const name = u.displayName && u.displayName() || '?';
    av.textContent = String(name).charAt(0).toUpperCase();
    av.style.display = 'grid';
    av.style.placeItems = 'center';
    av.style.color = '#fff';
    av.style.fontSize = '11px';
    av.style.fontWeight = '600';
    av.style.background = u.color && u.color() || 'var(--bespoke-color-brand)';
  }
  return av;
}

/** The primary tag of a discussion (the one with a real position), or null. */
function primaryTag(d) {
  if (!d || !d.tags) return null;
  const tags = d.tags() || [];
  return tags.find(t => t && t.position && t.position() !== null) || tags[0] || null;
}
export default function enhanceDiscussionList() {
  // Cover images / participant avatars / per-row tag colour on each row.
  // (Category sections are now a dedicated category index — see BespokeCategories
  // — rather than fragile in-list grouping.)
  extend(DiscussionListItem.prototype, 'oncreate', decorate);
  extend(DiscussionListItem.prototype, 'onupdate', decorate);
}